import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {AbstractComponent} from "../abstract.component";
import {
  Competitor,
  CompetitorSaveControlPointResultStatus,
  ControlPointTime,
  Lookups,
  RaceSettings,
  ResultProcessingType,
  WaypointType
} from "@model";

@Component({
  selector: 'edit-competitor-measurement-times-modal',
  templateUrl: './edit-competitor-measurement-times-modal.component.html',
  styleUrls: ['./edit-competitor-measurement-times-modal.component.scss']
})
export class EditCompetitorMeasurementTimes extends AbstractComponent implements OnInit {
  @Input({ required: true }) title: string = "";
  @Input() confirmButtonTitle?: string;
  @Input({ required: true }) competitor?: Competitor;
  @Input({ required: true }) languages: Lookups[] = [];
  @Input() raceSettings: RaceSettings | undefined;

  @Output() onSave = new EventEmitter<Competitor>();

  private _saveResult: Map<number, CompetitorSaveControlPointResultStatus> = new Map();
  startDate: Date | undefined;
  finishDate: Date | undefined;
  saveEnabled: boolean = false;
  isFinishWaypoint: boolean = false;
  displayDistanceInput: boolean = false;

  @Input()
  set saveResult(value: Map<number, CompetitorSaveControlPointResultStatus> | undefined) {
    if (value && this.mapHasChanged(value)) {
      this._saveResult = new Map(value);
    }
  }

  get saveResult(): Map<number, CompetitorSaveControlPointResultStatus> {
    return this._saveResult;
  }

  controlTimes?: ControlPointTime[];
  applyChanges = false;
  inputForm = new FormGroup({
    time: new FormControl(),
    chipTime: new FormControl(),
    selected: new FormControl(),
    penaltyMinutes: new FormControl(),
    lapDistancePassed: new FormControl(),
  });

  constructor(public activeModal: NgbActiveModal, public formBuilder: FormBuilder) {
    super();
  }

  ngOnInit(): void {
    this.controlTimes = this.competitor?.controlPointsTimes?.map(item => ({
      id: item.id ?? -1,
      time: item.time ? new Date(item.time) : undefined,
      chipTime: item.chipTime ? new Date(item.chipTime) : undefined,
      controlWaypoint:item.controlWaypoint,
      lap:item.lap,
      penaltyMinutes: item.penaltyMinutes,
      lapDistancePassed: item.lapDistancePassed
    })) || [];

    if (this.raceSettings?.lapLength) {
      this.inputForm.controls.lapDistancePassed.addValidators([Validators.max(this.raceSettings!.lapLength!)])
    }
    this.inputForm.controls.selected.valueChanges.subscribe(value => this.updateFormControls(value));

    this.inputForm.controls.time.valueChanges.subscribe(time => this.updateControlTime('time', time));
    this.inputForm.controls.chipTime.valueChanges.subscribe(time => this.updateControlTime('chipTime', time));
    this.inputForm.controls.penaltyMinutes.valueChanges.subscribe(penaltyMinutes => this.updateControlTime('penaltyMinutes', penaltyMinutes));
    this.inputForm.controls.lapDistancePassed.valueChanges.subscribe(lapDistancePassed => this.updateControlTime('lapDistancePassed', lapDistancePassed));

    this.disableFormControls();
  }

  updateFormControls(value: number): void {
    const controlTime: ControlPointTime | undefined = this.controlTimes?.find(ct => ct.id === value && ct.id !== -1);
    if (!controlTime) {
      this.inputForm.controls.selected.setValue(undefined, {onlySelf: true, emitEvent: false});
      this.isFinishWaypoint = false;
      return;
    };

    if (controlTime.controlWaypoint?.waypointTypeId === WaypointType.START_WAYPOINT) {
      this.startDate = undefined
    }
    else {
      this.startDate = this.controlTimes?.find((item: ControlPointTime) => item.controlWaypoint?.waypointTypeId === WaypointType.START_WAYPOINT)?.time;
    }
    if (controlTime.controlWaypoint?.waypointTypeId === WaypointType.FINISH_WAYPOINT) {
      this.isFinishWaypoint = true;
      this.finishDate = undefined;
    }
    else {
      this.finishDate = this.controlTimes?.find((item: ControlPointTime) => item.controlWaypoint?.waypointTypeId === WaypointType.FINISH_WAYPOINT)?.time;
    }
    if (this.raceSettings?.resultProcessingType === ResultProcessingType.TIME_LIMITED) {
      if (controlTime.lapDistancePassed) {
        this.displayDistanceInput = true;
      }
      else {
        const lapControlTimes = this.controlTimes?.filter(cpt => cpt.lap != null && cpt.lap > 0) || [];
        const isLastLap = lapControlTimes[lapControlTimes.length - 1]?.id === controlTime.id;

        this.displayDistanceInput = !lapControlTimes
            .some(cpt =>
                cpt.lapDistancePassed != null &&
                cpt.lapDistancePassed > 0
            ) && isLastLap;
      }
    } else {
      this.displayDistanceInput = false;
    }
    this.applyChanges = false;
    this.toggleControl(this.inputForm.controls.chipTime, controlTime.chipTime);
    this.toggleControl(this.inputForm.controls.time, controlTime.time);
    this.toggleControl(this.inputForm.controls.penaltyMinutes, true);
    this.toggleControl(this.inputForm.controls.lapDistancePassed, true);
    this.applyChanges = true;

    this.inputForm.patchValue({
      chipTime: controlTime.chipTime,
      time: controlTime.time,
      penaltyMinutes: controlTime.penaltyMinutes,
      lapDistancePassed: controlTime.lapDistancePassed
    });

    this.inputForm.updateValueAndValidity();
  }

  toggleControl(control: FormControl, condition: any): void {
    condition ? control.enable({onlySelf: true, emitEvent: false}) : control.disable({onlySelf: true, emitEvent: false});
  }

  updateControlTime(controlName: string, value: any): void {
    if (!this.applyChanges) return;
    const selected = this.inputForm.controls.selected.value;
    if (!Number.isInteger(selected) || value === undefined) return;

    const controlTime = this.controlTimes?.find(ct => ct.id === selected && ct.id !== -1);
    if (!controlTime) return;

    if (controlName === 'time' || controlName === 'chipTime' || controlName === 'penaltyMinutes' || controlName === 'lapDistancePassed') {
      controlTime[controlName] = value;
      this.controlTimes = ControlPointTime.sortControlPointTimes(this.controlTimes);
      if (RaceSettings.isRoundRace(this.raceSettings?.minimumLapDuration)) {
        let lap = 1;
        this.controlTimes.forEach(ct => {
          if (!ct.controlWaypoint && ct.lap) {
            ct.lap = lap;
            lap++;
          }
        });
      }
      this.validate()
    }

    this.inputForm.updateValueAndValidity();
  }

  validate() {
    const startDate = this.controlTimes?.find(cpt => cpt.controlWaypoint?.waypointTypeId === WaypointType.START_WAYPOINT)?.time;
    const finishDate = this.controlTimes?.find(cpt => cpt.controlWaypoint?.waypointTypeId === WaypointType.FINISH_WAYPOINT)?.time;
    let enableSave : boolean = true;
    const minimumLapDuration: number | undefined = RaceSettings.isRoundRace(this.raceSettings?.minimumLapDuration) ?
        Number(this.raceSettings?.minimumLapDuration) || undefined : undefined;

    const distanceAlreadyInserted: boolean = this.raceSettings?.resultProcessingType === ResultProcessingType.TIME_LIMITED &&
        !!this.controlTimes?.some(cpt =>
            cpt.lapDistancePassed !== undefined &&
            cpt.lapDistancePassed !== null &&
            cpt.lapDistancePassed > 0
        );

    this.controlTimes?.forEach((cpt, i) => {

      if (!cpt.id || !cpt.time) return;
      let previous : ControlPointTime | undefined;
      if (i > 0) {
        previous = this.controlTimes?.at(i-1);
      }

      let status = CompetitorSaveControlPointResultStatus.OK;

      const waypointType : WaypointType | undefined = cpt.controlWaypoint?.waypointTypeId;

      if (waypointType === WaypointType.START_WAYPOINT && finishDate && cpt.time.getTime() >= finishDate.getTime()) {
        status = CompetitorSaveControlPointResultStatus.AFTER_FINISH_TIME;
      } else if (waypointType === WaypointType.FINISH_WAYPOINT && startDate && cpt.time.getTime() <= startDate.getTime()) {
        status = CompetitorSaveControlPointResultStatus.BEFORE_START_TIME;
      } else if (waypointType === WaypointType.CONTROL_WAYPOINT || cpt.lap != undefined) {
        if (startDate && cpt.time.getTime() <= startDate.getTime()) {
          status = CompetitorSaveControlPointResultStatus.BEFORE_START_TIME;
        } else if (finishDate && cpt.time.getTime() >= finishDate.getTime()) {
          status = CompetitorSaveControlPointResultStatus.AFTER_FINISH_TIME;
        }
        if (cpt.lap && cpt.time && minimumLapDuration && previous?.time) {
            const timeDifference = (cpt.time.getTime() - previous.time.getTime()) / 1000;
            status = timeDifference < minimumLapDuration ? CompetitorSaveControlPointResultStatus.MINIMAL_LAP_DURATION_ERROR : status;
          }
        if (distanceAlreadyInserted && cpt.lapDistancePassed && (i < (this.controlTimes!.length - 2))) {
          status = CompetitorSaveControlPointResultStatus.FINISH_TIME_BEFORE_OTHER;
          }
        }

      this._saveResult.set(cpt.id, status);
      if (status !== CompetitorSaveControlPointResultStatus.OK) {
        enableSave = false;
      }
      if (distanceAlreadyInserted && cpt.lapDistancePassed && this.raceSettings?.lapLength && cpt.lapDistancePassed > this.raceSettings.lapLength) {
        enableSave = false;
      }
    });
    this.saveEnabled = enableSave;
  }


  disableFormControls(): void {
    this.inputForm.controls.chipTime.disable();
    this.inputForm.controls.time.disable();
    this.inputForm.controls.penaltyMinutes.disable();
    this.inputForm.controls.lapDistancePassed.disable();
    this.inputForm.updateValueAndValidity();
  }

  onConfirmClick(): void {
    if (this.competitor) this.competitor.controlPointsTimes = this.controlTimes?.map(cpt => ({ ...cpt, id: cpt.id === -1 ? undefined : cpt.id }));
    this.onSave.emit(this.competitor);
  }

  onDismissClick(): void {
    this.activeModal.dismiss();
  }

  private mapHasChanged(newMap: Map<number, CompetitorSaveControlPointResultStatus>): boolean {
    if (this._saveResult.size !== newMap.size) return true;
    for (const [key, value] of newMap.entries()) {
      if (this._saveResult.get(key) !== value) return true;
    }
    return false;
  }

  protected readonly WaypointType = WaypointType;
  protected readonly CompetitorSaveControlPointResultStatus = CompetitorSaveControlPointResultStatus;
  protected readonly ResultProcessingType = ResultProcessingType;
}
